import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { 
  TrendingUp, AlertTriangle, Package, Clock, 
  Building2, Flame, ShoppingCart, Globe,
  RefreshCw, Calendar, BarChart3
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANCHES = {
  "all": "All Branches",
  "QC": "Quezon City",
  "PQ": "Parañaque",
  "CV": "Cavite",
  "PM": "Pampanga",
  "VZ": "Valenzuela",
  "OTG-A": "Orion On The Go - A",
  "OTG-B": "Orion On The Go - B",
  "OTG-C": "Orion On The Go - C"
};

export default function SalesAnalytics() {
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [topSellers, setTopSellers] = useState([]);
  const [needsRestock, setNeedsRestock] = useState([]);
  const [branchStats, setBranchStats] = useState([]);
  const [summary, setSummary] = useState({ totalSales: 0, totalItems: 0, avgPerDay: 0, totalTransactions: 0 });

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [topSellRes, daysToLastRes, branchRes] = await Promise.all([
        axios.get(`${API}/analytics/top-selling`, { params: { branch: selectedBranch, days, limit: 20 } }),
        axios.get(`${API}/analytics/days-to-last`, { params: { branch: selectedBranch, days } }),
        axios.get(`${API}/analytics/branch-overview`, { params: { days } })
      ]);

      // Process Top Sellers
      const sellers = topSellRes.data?.items || [];
      setTopSellers(sellers);

      // Calculate summary
      const totalSales = sellers.reduce((sum, item) => sum + (item.total_qty || 0), 0);
      const totalTransactions = sellers.reduce((sum, item) => sum + (item.transactions || 0), 0);
      setSummary({
        totalSales,
        totalItems: sellers.length,
        avgPerDay: Math.round(totalSales / days * 10) / 10,
        totalTransactions
      });

      // Process Needs Restock - items with low days left
      // Backend returns: critical, low, out_of_stock, ok - combine them
      const daysToLastData = daysToLastRes.data || {};
      const daysToLastItems = [
        ...(daysToLastData.critical || []),
        ...(daysToLastData.low || []),
        ...(daysToLastData.out_of_stock || []),
        ...(daysToLastData.ok || [])
      ];
      const restockItems = daysToLastItems
        .filter(item => {
          const daysLeft = item.days_left;
          // Include items that are:
          // 1. Out of stock (0 stock)
          // 2. Running out soon (less than 14 days)
          // 3. Have actual sales movement
          return (item.current_stock === 0) || 
                 (typeof daysLeft === 'number' && daysLeft <= 14 && item.avg_daily > 0);
        })
        .sort((a, b) => {
          // Sort by urgency: out of stock first, then by days left
          if (a.current_stock === 0 && b.current_stock !== 0) return -1;
          if (b.current_stock === 0 && a.current_stock !== 0) return 1;
          const daysA = typeof a.days_left === 'number' ? a.days_left : 999;
          const daysB = typeof b.days_left === 'number' ? b.days_left : 999;
          return daysA - daysB;
        })
        .slice(0, 15);
      setNeedsRestock(restockItems);

      // Branch stats
      setBranchStats(branchRes.data?.branches || []);

    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedBranch, days]);

  // Get urgency style
  const getUrgencyStyle = (item) => {
    if (item.current_stock === 0) {
      return { bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-700', label: 'OUT OF STOCK', labelBg: 'bg-red-600' };
    }
    const daysLeft = item.days_left;
    if (typeof daysLeft === 'number') {
      if (daysLeft <= 3) return { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-600', label: 'CRITICAL', labelBg: 'bg-red-500' };
      if (daysLeft <= 7) return { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-600', label: 'URGENT', labelBg: 'bg-orange-500' };
      if (daysLeft <= 14) return { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-700', label: 'LOW', labelBg: 'bg-yellow-500' };
    }
    return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', label: 'OK', labelBg: 'bg-gray-400' };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-6" data-testid="sales-dashboard">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
              Sales Dashboard
            </h1>
            <p className="text-gray-500 mt-1">Tingnan kung anong mabenta at kung anong kailangan i-restock</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Period Selector */}
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="w-[130px] bg-white">
                <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>

            {/* Branch Selector */}
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="w-[180px] bg-white">
                <Building2 className="h-4 w-4 mr-2 text-gray-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(BRANCHES).map(([code, name]) => (
                  <SelectItem key={code} value={code}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Refresh */}
            <Button 
              variant="outline" 
              size="icon"
              onClick={fetchData}
              disabled={loading}
              className="bg-white"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Total Sold</p>
                <p className="text-3xl font-bold">{summary.totalSales.toLocaleString()}</p>
                <p className="text-green-200 text-xs mt-1">units in {days} days</p>
              </div>
              <ShoppingCart className="h-10 w-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Avg Per Day</p>
                <p className="text-3xl font-bold">{summary.avgPerDay}</p>
                <p className="text-blue-200 text-xs mt-1">units/day</p>
              </div>
              <TrendingUp className="h-10 w-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Transactions</p>
                <p className="text-3xl font-bold">{summary.totalTransactions.toLocaleString()}</p>
                <p className="text-purple-200 text-xs mt-1">total orders</p>
              </div>
              <Package className="h-10 w-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-orange-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Need Restock</p>
                <p className="text-3xl font-bold">{needsRestock.length}</p>
                <p className="text-red-200 text-xs mt-1">items low/out</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - Two Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* TOP SELLERS */}
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white pb-4">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Flame className="h-6 w-6" />
              🔥 Top Sellers
              <span className="ml-auto text-sm font-normal bg-white/20 px-3 py-1 rounded-full">
                Last {days} days
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : topSellers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <Package className="h-12 w-12 mb-2 opacity-50" />
                <p>No sales data for this period</p>
              </div>
            ) : (
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {topSellers.map((item, idx) => (
                  <div 
                    key={idx} 
                    className={`flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors ${idx < 3 ? 'bg-green-50/50' : ''}`}
                  >
                    {/* Rank Badge */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                      idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                      idx === 1 ? 'bg-gray-300 text-gray-700' :
                      idx === 2 ? 'bg-orange-400 text-orange-900' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {idx + 1}
                    </div>
                    
                    {/* Item Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{item.item_name}</p>
                      <p className="text-xs text-gray-500">
                        {item.transactions} orders
                        {item.channels?.includes('walk-in') && <span className="ml-1">🏪</span>}
                        {item.channels?.includes('online') && <span className="ml-1">🌐</span>}
                      </p>
                    </div>
                    
                    {/* Quantity */}
                    <div className="text-right">
                      <p className="text-xl font-bold text-green-600">{item.total_qty}</p>
                      <p className="text-xs text-gray-400">units</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* NEEDS RESTOCK */}
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-red-500 to-orange-600 text-white pb-4">
            <CardTitle className="flex items-center gap-2 text-xl">
              <AlertTriangle className="h-6 w-6" />
              ⚠️ Needs Restock
              <span className="ml-auto text-sm font-normal bg-white/20 px-3 py-1 rounded-full">
                Running low
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : needsRestock.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-green-600">
                <Package className="h-12 w-12 mb-2" />
                <p className="font-semibold">All stocks are healthy! ✅</p>
                <p className="text-sm text-gray-500">No items need restocking</p>
              </div>
            ) : (
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {needsRestock.map((item, idx) => {
                  const style = getUrgencyStyle(item);
                  return (
                    <div 
                      key={idx} 
                      className={`flex items-center gap-3 p-3 ${style.bg} border-l-4 ${style.border}`}
                    >
                      {/* Urgency Label */}
                      <div className={`px-2 py-1 rounded text-xs font-bold text-white ${style.labelBg}`}>
                        {style.label}
                      </div>
                      
                      {/* Item Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{item.item_name}</p>
                        <p className="text-xs text-gray-500">
                          {item.avg_daily > 0 ? `${item.avg_daily}/day avg` : 'No recent sales'}
                          {item.current_stock > 0 && typeof item.days_left === 'number' && (
                            <span className={`ml-2 ${style.text} font-medium`}>
                              ~{Math.round(item.days_left)} days left
                            </span>
                          )}
                        </p>
                      </div>
                      
                      {/* Stock Info */}
                      <div className="text-right">
                        <p className={`text-xl font-bold ${item.current_stock === 0 ? 'text-red-600' : style.text}`}>
                          {item.current_stock}
                        </p>
                        <p className="text-xs text-gray-400">in stock</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Branch Stats */}
      {selectedBranch === "all" && branchStats.length > 0 && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Sales by Branch
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {branchStats.map(branch => (
              <Card key={branch.branch} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-lg">{branch.branch}</span>
                    <span className="text-xs text-gray-500">{branch.item_count} items</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-green-600 flex items-center gap-1">
                        <ShoppingCart className="h-3 w-3" /> Walk-In
                      </span>
                      <span className="font-semibold">{branch.walk_in_sales}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-600 flex items-center gap-1">
                        <Globe className="h-3 w-3" /> Online
                      </span>
                      <span className="font-semibold">{branch.online_sales}</span>
                    </div>
                    <div className="flex justify-between pt-1 border-t font-bold">
                      <span className="text-indigo-600">Total</span>
                      <span className="text-indigo-600">{branch.total_out}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
